import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { resetPassword } from "../lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      toast.success("Password updated — you can now sign in.");
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <img src="/branding/tow-hook-icon-1024.png" alt="Tow-Hook" className="h-14 w-14 rounded-xl" />
          <div className="font-display font-black text-3xl tracking-tight text-white leading-none">Tow-Hook</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-xl">
          {done ? (
            <div data-testid="reset-password-done">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mb-3" />
              <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Password updated</h1>
              <p className="text-sm text-slate-500 mt-2">Taking you to the sign-in screen…</p>
            </div>
          ) : !token ? (
            <div data-testid="reset-password-notoken">
              <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Invalid link</h1>
              <p className="text-sm text-slate-500 mt-2">This reset link is missing its token. Please request a new one.</p>
              <Link to="/forgot-password" className="inline-block mt-5 text-sm font-semibold text-slate-900 hover:text-blue-600">Request a new link</Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Set a new password</h1>
              <p className="text-sm text-slate-500 mt-1">Choose a strong password you don't use elsewhere.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
                <div>
                  <Label className="uppercase-label">New password</Label>
                  <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="reset-password" required />
                </div>
                <div>
                  <Label className="uppercase-label">Confirm password</Label>
                  <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" data-testid="reset-password-confirm" required />
                </div>
                {err && (
                  <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5" data-testid="reset-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{err}</span>
                  </div>
                )}
                <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="reset-submit">
                  {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Updating…</>) : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
